import { GetResourceOptionsEndpoint, ResourceOption } from '@kouto/types';
import useSWR from 'swr';
import { get } from 'utils/axios';
import { convertIntervalToMinutes } from 'utils/formatDuration';
import { z } from 'zod';

type ResourceOptionsResponse = z.infer<
  typeof GetResourceOptionsEndpoint.responseSchema
>;

interface UseResourceOptionsProps {
  experienceId?: string;
  sessionDuration?: string | number;
  skip?: boolean;
  startDateTime?: string;
}

const fetchResourceOptions = async (url: string) => {
  const response = await get<ResourceOptionsResponse>(url);
  return response.items;
};

const useResourceOptions = ({
  experienceId,
  sessionDuration,
  skip = false,
  startDateTime,
}: UseResourceOptionsProps) => {
  const duration =
    typeof sessionDuration === 'string'
      ? convertIntervalToMinutes(sessionDuration)
      : sessionDuration ?? 0;

  const getUrl = (dateTime?: string) => {
    if (skip || !experienceId || !dateTime || duration <= 0) return null;
    return GetResourceOptionsEndpoint.url({
      experienceId,
      startDateTime: dateTime,
      duration,
    });
  };

  const url = getUrl(startDateTime);
  const swr = useSWR<ResourceOption[]>(url, fetchResourceOptions);

  const getResourceOptions = async (
    selectedStartDateTime = startDateTime,
  ): Promise<ResourceOption[]> => {
    const selectedUrl = getUrl(selectedStartDateTime);
    if (!selectedUrl) return [];
    if (selectedUrl === url && swr.data && !swr.isValidating) {
      return swr.data;
    }
    try {
      return await fetchResourceOptions(selectedUrl);
    } catch {
      // The booking API treats a missing pick as "any unit", so a failed lookup must not block booking.
      return [];
    }
  };

  return { ...swr, getResourceOptions };
};

export default useResourceOptions;
